import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import portalApi from './portalApi';

const PortalAuthContext = createContext(null);

export function PortalAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('portal_access_token');
      if (!token) {
        setLoading(false);
        return;
      }
      const res = await portalApi.get('/portal/auth/me');
      setUser(res.data.data);
    } catch (e) {
      localStorage.removeItem('portal_access_token');
      localStorage.removeItem('portal_refresh_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const res = await portalApi.post('/portal/auth/login', { email, password });
    const { access_token, refresh_token, user: userData } = res.data;
    localStorage.setItem('portal_access_token', access_token);
    localStorage.setItem('portal_refresh_token', refresh_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await portalApi.post('/portal/auth/logout');
    } catch (e) {
      // Ignore
    }
    localStorage.removeItem('portal_access_token');
    localStorage.removeItem('portal_refresh_token');
    setUser(null);
  };

  const isParticipant = user?.role === 'PARTICIPANT';
  const isFamily = user?.role === 'FAMILY';
  const isGuardian = user?.role === 'GUARDIAN';
  const isReferrer = user?.role === 'REFERRER';
  const hasParticipantAccess = isParticipant || isFamily || isGuardian;

  return (
    <PortalAuthContext.Provider value={{
      user, loading, login, logout, fetchUser,
      isParticipant, isFamily, isGuardian, isReferrer, hasParticipantAccess,
    }}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth() {
  const context = useContext(PortalAuthContext);
  if (!context) throw new Error('usePortalAuth must be used within PortalAuthProvider');
  return context;
}
